// @flow

import styled from '@emotion/styled'
import { Link } from 'gatsby'
import * as React from 'react'

import type { PricingPlan } from '@toggl/auth/src/types'
import type { DataLayer } from '@toggl/google-tag-manager/src/types'
import { colors, media } from '@toggl/style'
import {
  Button,
  Form,
  IconApple,
  IconGoogle,
  Input,
  Password,
  Link as UiLink,
  text,
} from '@toggl/ui'

import { useSignupForm } from './hooks/useSignupForm'
import PasswordRulesTooltip from './PasswordRulesTooltip'

declare var window: {
  dataLayer: DataLayer,
  location: Location,
}
declare var process: WebProcess

export default function SignupFormCondensed({
  textColor,
  initialError,
  planId,
  withSocials = true,
  SubmitButton = Button.Primary,
  shortCTA = false,
  className,
  ...props
}: {
  textColor?: string,
  initialError?: string,
  planId: PricingPlan,
  withSocials?: boolean,
  shortCTA?: boolean,
  className?: string,
  SubmitButton?: typeof Button.Primary | typeof Button.Alternate,
}) {
  const [formik, ready, googleSignup, appleSignup] = useSignupForm({
    planId,
    initialError,
  })

  return (
    <Root color={textColor} className={className}>
      <Form
        {...props}
        error={
          formik.errors.form ? (
            <FormError>
              An account with the email already exists.{' '}
              <Link to={'/track/login/'}>Log in here</Link>
            </FormError>
          ) : null
        }
        onSubmit={formik.handleSubmit}
        action="javascript:void(0)"
      >
        <LoginOptionsGrid>
          <Input
            id="email"
            name="email"
            type="email"
            className="input-wrapper"
            placeholder={'Email'}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            value={formik.values.email}
            error={
              formik.errors.email && formik.touched.email ? (
                <FormError>{formik.errors.email}</FormError>
              ) : null
            }
          />

          <PasswordRulesTooltip
            value={formik.values.password}
            showError={formik.touched.password}
          >
            {({ onFocus, onBlur }) => (
              <Password
                id="password"
                name="password"
                type="password"
                className="input-wrapper"
                placeholder={'A strong password'}
                onBlur={() => {
                  formik.setFieldTouched('password')
                  onBlur()
                }}
                onFocus={onFocus}
                onChange={formik.handleChange}
                value={formik.values.password}
                error={!!(formik.errors.password && formik.touched.password)}
              />
            )}
          </PasswordRulesTooltip>

          {!!SubmitButton && (
            <SubmitButton
              type="submit"
              disabled={!ready || formik.isSubmitting}
            >
              {shortCTA ? 'Get started' : 'Sign up with email'}
            </SubmitButton>
          )}

          {withSocials && (
            <SocialSignupWrapper>
              <text.P3 color={textColor}>Or sign up with:</text.P3>

              <SocialLogin
                type="button"
                disabled={!ready || formik.isSubmitting}
                onClick={googleSignup}
                color={textColor}
              >
                <IconGoogle color={textColor} />
              </SocialLogin>
              <SocialLogin
                type="button"
                disabled={!ready || formik.isSubmitting}
                onClick={appleSignup}
                color={textColor}
              >
                <IconApple color={textColor} />
              </SocialLogin>
            </SocialSignupWrapper>
          )}
        </LoginOptionsGrid>

        <ToS color={textColor}>
          By signing up, you agree to our{' '}
          <Link to={'/track/legal/terms/'}>
            <UiLink.Underlined>terms of service</UiLink.Underlined>
          </Link>
          {', '}
          <Link to={'/track/legal/privacy/'}>
            <UiLink.Underlined>privacy policy</UiLink.Underlined>
          </Link>{' '}
          and to receiving marketing communication from Toggl Track. You can opt
          out anytime.
        </ToS>
      </Form>
    </Root>
  )
}

const Root: React.ComponentType<any> = styled.div`
  form {
    label,
    input,
    select {
      color: ${(p) => p.color};
      &:focus {
        border-color: ${(p) => p.color};
      }
    }
  }
`

const LoginOptionsGrid = styled.div`
  margin-bottom: 30px;
  ${media.mq[1]} {
    display: grid;
    grid-template-columns: 1.2fr 0.25fr 0.8fr 1fr;
    margin-bottom: 45px;
    > div:nth-of-type(-n + 2) {
      grid-column: 1 / 4;
    }
    button {
      grid-column: 1 / 2;
      grid-row: 3 / 4;
    }
  }
`

const SocialSignupWrapper = styled.div`
  display: flex;
  column-gap: 10px;
  align-items: center;
  margin: 30px 0 0;
  p {
    margin-right: 5px;
    line-height: 1.3;
  }
  ${media.mq[1]} {
    margin: 0;
    grid-column: 3 / 5;
    grid-row: 3 / 4;
    align-self: end;
  }
  button {
    flex: 0 0 52px;
  }
`

const SocialLogin = styled(Button.SocialLogin)`
  width: 52px !important;
  background: transparent;
  border: 1px solid ${(p) => p.color};
  margin-top: 0 !important;
  svg {
    margin-right: 0;
  }
  &:hover svg path {
    fill: ${colors.darkPurple};
  }
`

const FormError = styled.div`
  color: ${colors.red};
  a {
    color: ${colors.red};
    text-decoration: underline;
  }
`

const ToS = styled.p`
  color: ${(p) => p.color};
  font-size: 12px;
  line-height: 1.4;
  a {
    color: ${(p) => p.color};
  }
`
